import { datadogRum } from "@datadog/browser-rum";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Component, ReactNode } from "react";

import { Alert, Button, Flex } from "~/ui";

interface AIChatErrorBoundaryProps {
  children: ReactNode;
}

interface AIChatErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary for AI chat component to prevent full app crashes.
 *
 * Catches any errors within the AI chat (parsing, network, rendering) and
 * displays a user-friendly fallback UI instead of crashing the entire page.
 *
 * This is a React best practice for critical interactive components.
 */
export class AIChatErrorBoundary extends Component<AIChatErrorBoundaryProps, AIChatErrorBoundaryState> {
  constructor(props: AIChatErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): AIChatErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to Datadog RUM for monitoring and debugging
    datadogRum.addError(error, {
      errorInfo,
      source: "ai-chat-error-boundary",
    });
  }

  handleReset = (): void => {
    // Reset error state to allow retry
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Flex direction="column" gap="sm" p="md">
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Assistant Unavailable">
            Assistant encountered an error. This has been logged and we&apos;ll look into it.
          </Alert>
          <Flex gap="xs">
            <Button compact size="xs" variant="light" onClick={this.handleReset}>
              Try Again
            </Button>
            <Button compact size="xs" variant="subtle" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Flex>
        </Flex>
      );
    }

    return this.props.children;
  }
}
